import React from "react";
import { Typography, Button, Spin } from "antd";
import { FileTextOutlined, DownloadOutlined } from "@ant-design/icons";
import { useLazyGetDownloadUrlQuery } from "../../store/api/fileApi";
import type { FileUploadResponse } from "../../store/api/fileApi";

const { Text } = Typography;

export interface UploadedFilesListProps {
  files: FileUploadResponse[];
  isLoading?: boolean;
  title?: string;
}

const UploadedFilesList: React.FC<UploadedFilesListProps> = ({
  files,
  isLoading = false,
  title,
}) => {
  const [getDownloadUrl] = useLazyGetDownloadUrlQuery();

  const handleDownload = async (fileId: number, fileName: string) => {
    try {
      const result = await getDownloadUrl(fileId).unwrap();
      const link = document.createElement("a");
      link.href = result.downloadUrl;
      link.download = fileName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to get download URL:", error);
    }
  };

  if (isLoading) {
    return <Spin tip="Loading documents..." />;
  }

  if (!files || files.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: "16px" }}>
      {title && (
        <Text strong style={{ marginBottom: "8px", display: "block" }}>
          {title} ({files.length})
        </Text>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: "8px",
          backgroundColor: "#fafafa",
          padding: "12px",
          borderRadius: "6px",
          border: "1px solid #f0f0f0",
        }}
      >
        {files.map((file) => {
          const ext = file.originalFileName.split(".").pop()?.toLowerCase();
          const isImage = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(ext || "");
          const isPdf = ext === "pdf";
          const isExcel = ["xlsx", "xls"].includes(ext || "");
          const isWord = ["doc", "docx"].includes(ext || "");
          let iconColor = "#8c8c8c";
          if (isImage) iconColor = "#52c41a";
          else if (isPdf) iconColor = "#ff4d4f";
          else if (isExcel) iconColor = "#52c41a";
          else if (isWord) iconColor = "#1890ff";

          return (
            <div
              key={file.fileId}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 12px",
                backgroundColor: "#fff",
                borderRadius: "4px",
                border: "1px solid #e8e8e8",
              }}
            >
              <FileTextOutlined style={{ fontSize: "16px", color: iconColor }} />
              <div style={{ flex: 1, overflow: "hidden" }}>
                <Text ellipsis style={{ display: "block", maxWidth: "150px" }}>
                  {file.originalFileName}
                </Text>
                <Text type="secondary" style={{ fontSize: "11px" }}>
                  {(file.fileSize / 1024).toFixed(1)} KB
                </Text>
              </div>
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(file.fileId, file.originalFileName)}
              >
                Download
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UploadedFilesList;
